/**
 * Read /api/system/config once at app boot, cache the result, and expose
 * a synchronous accessor + React hook.  Used by feature flags that gate
 * UI surfaces (e.g. the `evaluate_js` step-type in the workflow editor).
 *
 * The config is intentionally tiny — it never carries secrets, only
 * non-sensitive UI flags.  We don't worry about cache invalidation; the
 * flags are derived from backend env vars that only change on a deploy,
 * and a full page reload picks up the new values.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const DEFAULT_CONFIG = {
  evaluate_js_enabled: false,
  allow_discovery: false,
  environment: "development",
  // R3 Phase 6A.5 — pre-fill default for the AI step-generation
  // target URL field on the workflow detail page.
  eyvo_demo_url: "",
};

let _cache = null;
let _inflight = null;

export async function fetchSystemConfig() {
  if (_cache) return _cache;
  if (!_inflight) {
    _inflight = api
      .get("/system/config")
      .then((res) => {
        _cache = { ...DEFAULT_CONFIG, ...(res.data || {}) };
        return _cache;
      })
      .catch(() => {
        // 401/etc. fall back to safe defaults; the editor will just hide
        // the gated surfaces, which is the right default-deny posture.
        _cache = { ...DEFAULT_CONFIG };
        return _cache;
      })
      .finally(() => {
        _inflight = null;
      });
  }
  return _inflight;
}

export function useSystemConfig() {
  const [config, setConfig] = useState(_cache || DEFAULT_CONFIG);
  const [ready, setReady] = useState(Boolean(_cache));
  useEffect(() => {
    let alive = true;
    fetchSystemConfig().then((c) => {
      if (alive) {
        setConfig(c);
        setReady(true);
      }
    });
    return () => {
      alive = false;
    };
  }, []);
  return { config, ready };
}
